import DOMPurify from 'dompurify';

const SUCCESS_STATUSES = new Set(['parsed', 'unchanged']);
const CONTENT_SELECTOR = 'img, video, audio, table, hr, a[href]';

/** Talon is the only quotation parser. Empty replies are successful results. */
export function getEmailTalonProjection(email = {}) {
  const value = email?.talon_projection ?? email?.talonProjection;
  const replyText = value?.reply_text ?? value?.replyText;
  const replyHTML = value?.reply_html ?? value?.replyHtml;
  const parserVersion = value?.parser_version ?? value?.parserVersion;
  const sourceDigest = value?.source_digest ?? value?.sourceDigest;
  const ready =
    value?.schema === 1 &&
    value?.parser === 'talon' &&
    SUCCESS_STATUSES.has(value.status) &&
    typeof replyText === 'string' &&
    typeof parserVersion === 'string' &&
    Boolean(parserVersion) &&
    typeof sourceDigest === 'string' &&
    Boolean(sourceDigest);
  return {
    ready,
    status: ready ? value.status : value?.status || 'pending',
    parserVersion: parserVersion || '',
    sourceDigest: sourceDigest || '',
    replyText: ready ? replyText : '',
    replyHTML: ready && typeof replyHTML === 'string' ? replyHTML : null,
    hasQuotes: ready && (value.has_quotes ?? value.hasQuotes) === true,
    hasReplyText:
      ready && (value.has_reply_text ?? value.hasReplyText) === true,
    hasReplyContent:
      ready && (value.has_reply_content ?? value.hasReplyContent) === true,
  };
}

function textToHTML(text = '') {
  const node = document.createElement('div');
  node.textContent = text;
  return node.innerHTML.replace(/\r\n?/g, '\n').replace(/\n/g, '<br>');
}

function displayHTML(html, text = '') {
  // Template contents are inert: remove tracking images before the browser
  // can begin fetching them, including when rendering quoted reply history.
  const template = document.createElement('template');
  template.innerHTML = DOMPurify.sanitize(html || '', {
    USE_PROFILES: { html: true },
  });
  const root = template.content;
  if (!root.textContent.trim() && !root.querySelector(CONTENT_SELECTOR)) {
    template.innerHTML = textToHTML(text);
  }
  // Never request our own open pixels while staff view replies/quoted history.
  // This changes presentation only; the original received HTML stays intact.
  root.querySelectorAll('img, source').forEach(node => {
    const sources = [node.getAttribute('src'), node.getAttribute('srcset')];
    if (
      sources.some(source =>
        /\/borderchat\/email-open\/[a-f0-9]{64}\.png/i.test(source || '')
      )
    ) {
      node.remove();
    }
  });
  // Presentation only: prevent email client wrappers from adding nested scrollbars.
  root.querySelectorAll('div, p, pre, section').forEach(node => {
    [
      'height',
      'min-height',
      'max-height',
      'overflow',
      'overflow-x',
      'overflow-y',
    ].forEach(property => node.style.removeProperty(property));
    if (!node.getAttribute('style')) node.removeAttribute('style');
  });
  return template.innerHTML;
}

/** Read the server projection; never infer reply boundaries in the browser. */
export function prepareEmailDisplay({ email = {}, text = '' } = {}) {
  const projection = getEmailTalonProjection(email);
  const fullHTML = displayHTML(
    (email.html_content ?? email.htmlContent)?.full,
    (email.text_content ?? email.textContent)?.full ?? text
  );
  let notice = '';
  if (!projection.ready) {
    notice =
      projection.status === 'pending'
        ? '正在识别邮件正文，当前显示完整邮件。'
        : '暂未识别邮件引用，当前显示完整邮件。';
  }
  return {
    ...projection,
    fullHTML,
    replyHTML: projection.ready
      ? displayHTML(projection.replyHTML, projection.replyText)
      : fullHTML,
    notice,
  };
}
